import { Link } from "react-router-dom";
import { Phone, MapPin, Instagram } from "lucide-react";
import { MODELS } from "../data/models";
import { SALES, DEALER } from "../data/constants";
import { useSettings, buildWaLink } from "../lib/SettingsContext";
import { useContent } from "../lib/ContentContext";
import { trackWhatsAppLead } from "../lib/AnalyticsInjector";

export default function Footer() {
  const { settings } = useSettings();
  const { t } = useContent();
  const dealerName = settings.dealer_name || DEALER.name;
  const address = settings.dealer_address || DEALER.address;
  const salesName = settings.sales_name || SALES.name;
  const salesTitle = settings.sales_title || SALES.title;
  const phone = settings.sales_phone || SALES.phone;
  const igUrl = settings.instagram_url || "https://instagram.com/bydcibubur.official";
  const igHandle = settings.instagram_handle || "@bydcibubur.official";
  return (
    <footer
      className="bg-[#0F0F0F] text-white mt-24"
      data-testid="main-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-medium tracking-tight">{t("nav.brand_main", "BYD")}</span>
              <span className="ml-2 text-xs uppercase tracking-[0.2em] text-white/60">
                {t("nav.brand_sub", "Cibubur")}
              </span>
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-white/70 max-w-sm">
              {t("footer.description", "Dealer Resmi BYD Haka Auto Cibubur.")}
            </p>
            <div className="mt-6 space-y-3 text-sm text-white/80">
              <a
                href={buildWaLink(settings)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppLead("footer")}
                className="flex items-start gap-3 hover:text-white"
                data-testid="footer-whatsapp"
              >
                <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#25D366]" />
                <span>
                  <span className="block text-white">{phone}</span>
                  <span className="text-xs text-white/50">
                    {salesName} — {salesTitle}
                  </span>
                </span>
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-white/60" />
                <span className="leading-relaxed">{address}</span>
              </div>
              <a
                href={igUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-white group"
                data-testid="footer-instagram"
                aria-label={`Instagram ${igHandle}`}
              >
                <Instagram className="w-4 h-4 shrink-0 text-pink-400 group-hover:scale-110 transition-transform" />
                <span className="text-white/80 group-hover:text-white">{igHandle}</span>
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5">
              {t("footer.section_featured", "Unit Terlaris")}
            </h4>
            <ul className="space-y-3 text-sm">
              {MODELS.filter((m) => m.featured)
                .sort((a, b) => a.priority - b.priority)
                .map((m) => (
                <li key={m.slug}>
                  <Link
                    to={`/model/${m.slug}`}
                    className="text-white/80 hover:text-white"
                    data-testid={`footer-model-${m.slug}`}
                  >
                    {m.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5">
              {t("footer.section_lineup", "Lineup")}
            </h4>
            <ul className="space-y-3 text-sm">
              {MODELS.filter((m) => !m.featured).map((m) => (
                <li key={m.slug}>
                  <Link
                    to={`/model/${m.slug}`}
                    className="text-white/80 hover:text-white"
                  >
                    {m.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5">
              {t("footer.section_info", "Informasi")}
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/models" className="text-white/80 hover:text-white">
                  {t("footer.link_all_models", "Semua Model")}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/80 hover:text-white" data-testid="footer-blog">
                  {t("footer.link_blog", "Blog")}
                </Link>
              </li>
              <li>
                <Link
                  to="/kebijakan-privasi"
                  className="text-white/80 hover:text-white"
                  data-testid="footer-privacy"
                >
                  {t("footer.link_privacy", "Kebijakan Privasi")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/50">
          <p>
            {t("footer.copyright_template", "© {year} {dealer}. Authorized BYD Dealer. Seluruh spesifikasi & harga sewaktu-waktu dapat berubah.")
              .replace("{year}", String(new Date().getFullYear()))
              .replace("{dealer}", dealerName)}
          </p>
          <p>
            {t("footer.consultant_template", "Konsultan Resmi: {name}, {title}.")
              .replace("{name}", salesName)
              .replace("{title}", salesTitle)}
          </p>
        </div>
      </div>
    </footer>
  );
}
